<div class="relative h-full w-full" role="dialog" aria-modal="true" aria-labelledby="share-group-title">
    <div class="absolute inset-0 z-10 flex items-center justify-center overflow-y-auto bg-zinc-200/40 p-4 backdrop-blur-md dark:bg-zinc-900/60"
        (click)="closeShare()">
        <section class="relative z-20 my-auto w-full max-w-xl primary-card-container-no-padding px-8 py-6 sm:px-10"
            (click)="ignoreBackdrop($event)">
            <button type="button" (click)="closeShare()"
                class="absolute top-3 right-3 rounded-full p-2 text-secondary hover:bg-zinc-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-600 dark:hover:bg-zinc-800">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                    stroke="currentColor" class="size-5" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
                <span class="sr-only">Close</span>
            </button>

            @if (emailSent()) {
            <div class="flex flex-col items-center py-8 text-center">
                <div class="flex size-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-400/10">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-6 text-green-600 dark:text-green-400" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                </div>
                <h2 id="share-group-title" class="mt-4 text-base font-semibold text-primary">Email sent</h2>
                <p class="mt-1 max-w-sm text-sm text-secondary">Your colleague can open the shortlist from the link.
                    They only need a first name to approve or decline names.</p>
                <button (click)="closeShare()" type="button" class="primary-button mt-6">Done</button>
            </div>
            } @else {
            <div class="pr-8">
                <h2 id="share-group-title" class="text-base font-semibold text-primary">Share {{ group().groupName }}</h2>
                <p class="mt-1 text-sm text-secondary">Send a link to your colleague or client to review the group.</p>
            </div>

            <div class="mt-6 rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <p class="m-0 text-sm font-medium text-primary">Copy link</p>
                <p class="mt-1 text-xs text-secondary">Anyone with the link can review this group.</p>
                <div class="mt-3 flex flex-col gap-2 sm:flex-row">
                    <input id="share-group-url" type="text" readonly
                        [value]="resolvingLink() ? 'Preparing link…' : shareUrl()"
                        class="input-primary min-w-0 flex-1 truncate text-sm text-primary"
                        [attr.aria-busy]="resolvingLink()">
                    <button type="button" (click)="copyShareLink()" [disabled]="resolvingLink() || !shareUrl()"
                        class="primary-button inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60">
                        @if (copiedLink()) {
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"
                            aria-hidden="true">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                        Copied
                        } @else {
                        Copy link
                        }
                    </button>
                </div>
            </div>

            <div class="mt-4 rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                        <p id="allow-reviewer-invite-label" class="m-0 text-sm font-medium text-primary">Invite Creators Automatically</p>
                        <p class="mt-1 text-xs text-secondary">Creators they approve will be invited to the brief you
                            choose.</p>
                        @if (savingInviteSettings()) {
                        <p class="mt-1 text-xs text-secondary">Saving…</p>
                        } @else if (inviteSettingsSaved()) {
                        <p class="mt-1 text-xs font-medium text-green-700 dark:text-green-300">Saved</p>
                        }
                    </div>
                    <button type="button" role="switch" [attr.aria-checked]="allowReviewerInvite()"
                        aria-labelledby="allow-reviewer-invite-label"
                        [attr.aria-describedby]="allowReviewerInvite() ? 'share-invite-brief-help' : null"
                        (click)="toggleAllowReviewerInvite()" [disabled]="savingInviteSettings()"
                        class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60"
                        [class]="allowReviewerInvite() ? 'bg-primary' : 'bg-zinc-200 dark:bg-zinc-700'">
                        <span class="inline-block size-5 rounded-full bg-white shadow-sm transition-transform"
                            [class]="allowReviewerInvite() ? 'translate-x-5' : 'translate-x-0.5'"></span>
                    </button>
                </div>

                @if (allowReviewerInvite()) {
                <div class="mt-4">
                    <label for="share-invite-brief" class="block text-sm font-medium text-primary">Choose a
                        brief</label>
                    @if (!loadingBriefs()) {
                    <select id="share-invite-brief" class="input-primary mt-2 w-full text-primary"
                        (change)="onInviteBriefChange($event)">
                        <option value="" [selected]="!selectedInviteBriefId()">Select a brief</option>
                        @for (brief of inviteBriefs(); track brief.id) {
                        <option [value]="brief.id" [selected]="selectedInviteBriefId() === brief.id">{{ brief.title }}
                        </option>
                        }
                    </select>
                    }
                    <p id="share-invite-brief-help" class="mt-1 text-xs text-secondary">
                        @if (loadingBriefs()) {
                        Loading briefs…
                        } @else if (inviteBriefs().length === 0) {
                        No open briefs on this brand. Create a brief first, then choose it here.
                        } @else if (!selectedInviteBriefId()) {
                        Choose the brief these invites should go to.
                        } @else {
                        Approving a creator will invite them to this brief.
                        }
                    </p>
                </div>
                }
            </div>

            <div class="mt-4">
                <p class="m-0 text-sm font-medium text-primary">Email a colleague</p>
                <label for="share-group-email" class="mt-3 block text-sm font-medium text-primary">Email</label>
                <input id="share-group-email" type="email" autocomplete="email" [formField]="shareForm.email"
                    placeholder="name@company.com" class="input-primary mt-2 text-primary">
                @if (shareForm.email().touched() && shareForm.email().invalid()) {
                <ul class="error-list">
                    @for (error of shareForm.email().errors(); track error) {
                    <li class="text-error-light">{{ error.message }}</li>
                    }
                </ul>
                }

                <label for="share-group-message" class="mt-4 block text-sm font-medium text-primary">Note
                    <span class="font-normal text-secondary">(optional)</span></label>
                <textarea id="share-group-message" rows="2" [formField]="shareForm.message"
                    placeholder="Anything they should know before reviewing"
                    class="input-textarea mt-2 text-primary"></textarea>
            </div>

            <div class="mt-6 flex items-center justify-between gap-3">
                <button (click)="closeShare()" type="button" class="secondary-button text-primary">Cancel</button>
                @if (sendingEmail()) {
                <p class="m-0 text-sm text-secondary">Sending…</p>
                } @else {
                <button (click)="sendEmail()" type="button"
                    [disabled]="shareForm().invalid() || resolvingLink() || !shareUrl()"
                    class="primary-button inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60">
                    Send email
                </button>
                }
            </div>
            }
        </section>
    </div>
</div>
